<template>
  <div>
    <b-card class="page-card">
      <div class="page-header">
        <h2>Parts orders</h2>
      </div>
      <b-row v-if="!$smallScreen">
        <b-form-group>
          <b-input-group>
            <b-form-input
              id="filterInput"
              v-model="filter"
              variant="position-relative"
              type="search"
              placeholder="Type to Filter"
              :debounce="500"
            ></b-form-input>
          </b-input-group>
        </b-form-group>
        <div class="ml-auto w-auto d-inline-flex">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="pageSize"
            aria-controls="order-history-table"
          ></b-pagination>
        </div>
      </b-row>
      <b-table
        class="border"
        no-border-collapse
        striped
        sticky-header="63vh"
        :items="partsOrderSearchResults"
        :per-page="pageSize"
        :current-page="currentPage"
        :fields="fields"
        :filter="filter"
        :filter-included-fields="filterOn"
        :sort-by.sync="sortBy"
        :busy="searchIsBusy"
        show-empty
        @filtered="onFiltered"
      >
        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>
        <template #head(_showMore)>
          <div class="text-center">
            <div class="btn-icon" @click="toggleExpandCollapseAll()">
              <img :src="iconString" />
            </div>
          </div>
        </template>
        <template #cell(_showMore)="data">
          <div class="text-center">
            <div class="btn-icon" @click="toggleMore(data.index)">
              <font-awesome-icon v-if="showingMore(data.index)" icon="minus" class="text-primary" />
              <font-awesome-icon v-else icon="plus" class="text-primary" />
            </div>
          </div>
        </template>
        <template #cell(partsOrderItems)="data">
          <table-cell-list
            :ref="`defect-list-${data.index}`"
            :row-index="data.index"
            :list="filteredPartsOrderItems(data.item.partsOrderItems)"
            :show-more.sync="data.item._showMore"
            @show="showMore(item)"
          >
            <template #default="{ item: partsOrderItem }">
              <div>
                <b>{{ partsOrderItem.partNumber }}</b>
                &nbsp;
                <span class="text-muted">{{ partsOrderItem.partDescription }}</span>
              </div>
            </template>
          </table-cell-list>
        </template>
        <template #cell(partsOrderId)="{ item }">
          <router-link :to="{ name: 'PartsOrderDetails', params: { partsOrderId: item.partsOrderId } }">
            {{ item.partsOrderId }}
          </router-link>
        </template>
        <template #cell(total)="{ item }">{{ (item.subTotal + item.amtTax) | currency }}</template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
import TableCellListComponent from '@/shared/components/TableCellListComponent';
import { mapActions, mapGetters } from 'vuex';
import { PartsOrderGetters, PartsOrderActions } from '@/shared/store/parts-order/types';
import { LookupGetters } from '@/shared/store/lookup/types';
import moment from 'moment';

import ErrorService from '@/shared/services/ErrorService';

export default {
  name: 'OrderHistory',
  components: {
    'table-cell-list': TableCellListComponent
  },
  data() {
    return {
      sortBy: '',
      filter: null,
      searchIsBusy: false,
      pageSize: 50,
      currentPage: 1,
      totalRows: 0,
      filterOn: ['partsOrderId', 'poNumber', 'posted', 'dateCreate', 'total'],
      filteredItems: [],
      expandAllIcon: '/images/expand-all.svg',
      collapseAllIcon: '/images/collapse-all.svg',
      iconString: null,
      isExpanded: false
    };
  },
  computed: {
    ...mapGetters({
      partsOrderSearchResults: PartsOrderGetters.GET_PARTS_ORDER_SEARCH_RESULTS,
      customer: LookupGetters.GET_ECOMMERCE_CUSTOMER
    }),
    fields() {
      let fields = [];
      fields.push({ key: '_showMore', label: '', thStyle: 'width: 50px' });
      fields.push({ key: 'partsOrderId', label: 'Order Id', sortable: true });
      fields.push({ key: 'poNumber', label: 'PO number', sortable: true });
      fields.push({ key: 'partsOrderItems', label: 'Parts', sortable: true });
      fields.push({
        key: 'posted',
        label: 'Status',
        sortable: true,
        sortByFormatted: true,
        sortDirection: 'desc',
        formatter: value => {
          if (value === 0) {
            return 'Open';
          }
          if (value === 1) {
            return 'Invoiced';
          }
          return value;
        }
      });
      fields.push({
        key: 'dateCreate',
        label: 'Date create',
        sortable: true,
        formatter: value => {
          return moment(value).format('MM/DD/YYYY');
        },
        filterByFormatted: true
      });
      fields.push({ key: 'total', label: 'Total', sortable: false });
      return fields;
    }
  },
  created() {
    this.iconString = this.expandAllIcon;
  },
  async mounted() {
    await this.runSearch();
  },
  methods: {
    ...mapActions([PartsOrderActions.SEARCH_PARTS_ORDERS]),
    toggleMore(index) {
      this.setShowMore(index, !this.showingMore(index));
    },
    setShowMore(index, value) {
      const rowRef = this.$refs[`defect-list-${index}`];
      if (rowRef?.setShowMore) {
        rowRef.setShowMore(value);
      }
    },
    showingMore(index) {
      return this.$refs[`defect-list-${index}`]?.showMore;
    },
    toggleExpandCollapseAll() {
      this.isExpanded = !this.isExpanded;
      this.iconString = this.isExpanded ? this.collapseAllIcon : this.expandAllIcon;

      this.partsOrderSearchResults.forEach((item, index) => {
        this.setShowMore(index, this.isExpanded);
      });
    },
    onFiltered(filteredItems) {
      this.filteredItems = filteredItems;
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
    filteredPartsOrderItems(items) {
      if (!items) {
        return [];
      }
      return items.filter(item => !(item.qtyBackorder === 0 && item.qtyShip === 0 && item.itmType !== 532));
    },
    async runSearch() {
      try {
        this.searchIsBusy = true;
        await this[PartsOrderActions.SEARCH_PARTS_ORDERS]({
          partsOrderStatus: 'All',
          Customer: this.customer.customerId
        });
        this.filteredItems = this.partsOrderSearchResults;
        this.totalRows = this.partsOrderSearchResults.length;
      } catch (error) {
        ErrorService.unknownSearchError(this);
      } finally {
        this.searchIsBusy = false;
      }
    }
  }
};
</script>
